import { parse422Errors } from 'src/api/utils/errorUtils';

// Server field names come from FluentValidation's `PropertyName` (PascalCase C#
// members, e.g. "FirstName", or dotted/indexed paths like "Levels[0].Name").
// Form field names are camelCase. Lowercase the first character of each
// path segment's identifier while preserving dots and array indices, so the
// result can be passed straight to Formik's `setFieldError`/`setErrors`
// (which understand dotted/bracketed paths).
const normalizeFieldPath = (field: string): string =>
  field
    .split('.')
    .map((segment) => {
      const match = segment.match(/^([^[]+)(\[.*\])?$/);
      if (!match) return segment;
      const [, name, index = ''] = match;
      return name.charAt(0).toLowerCase() + name.slice(1) + index;
    })
    .join('.');

// Maps a caught error to a flat field -> message record when it represents a
// 422/400 validation failure with per-field details. Returns null for
// non-validation errors or validation errors with only a top-level message
// (no field-level detail to attach inline).
export const getValidationErrors = (
  err: unknown,
  joinWith = ', ',
): Record<string, string> | null => {
  const parsed = parse422Errors(err);
  if (!parsed || Object.keys(parsed.fieldErrors).length === 0) return null;

  const result: Record<string, string> = {};
  Object.entries(parsed.fieldErrors).forEach(([field, messages]) => {
    result[normalizeFieldPath(field)] = messages.join(joinWith);
  });
  return result;
};

// Messages are newline-joined so a multi-rule failure stays readable inside a
// single Mantine `error` slot.
export const toFormikFieldErrors = (err: unknown): Record<string, string> =>
  getValidationErrors(err, '\n') ?? {};

export interface ApplyValidationErrorsResult {
  // True when at least one server field error was matched to a known form field.
  applied: boolean;
  // Messages for server fields that don't exist on the form (nothing to attach
  // them to) - callers should still surface these, e.g. via a toast.
  unmatchedMessages: string[];
}

// Applies a caught error's field-level validation messages to a Formik form via
// `setFieldError`, matching only fields present in `knownFields` (top-level key
// of each field path) so unrelated form errors aren't clobbered and unknown
// server fields aren't silently dropped.
export const applyValidationErrors = (
  err: unknown,
  knownFields: string[],
  setFieldError: (field: string, message: string) => void,
): ApplyValidationErrorsResult => {
  const validationErrors = getValidationErrors(err);
  if (!validationErrors) return { applied: false, unmatchedMessages: [] };

  let applied = false;
  const unmatchedMessages: string[] = [];

  Object.entries(validationErrors).forEach(([field, message]) => {
    const topLevelKey = field.split(/[.[]/)[0];
    if (knownFields.includes(topLevelKey)) {
      setFieldError(field, message);
      applied = true;
    } else {
      unmatchedMessages.push(message);
    }
  });

  return { applied, unmatchedMessages };
};
